import { useEffect, useMemo, useState } from "react";
import { fetchUsageTrends } from "../services/usageTrendsService";

// Supports two call styles:
// 1) useUsageTrends(30, "day", "visits") -> last N days
// 2) useUsageTrends({ start, end, interval, metric })
export default function useUsageTrends(defaultDaysOrOptions = 30, defaultInterval = "day", defaultMetric = "visits") {
	const opts = useMemo(() => {
		if (typeof defaultDaysOrOptions === "object" && defaultDaysOrOptions !== null) {
			return {
				start: defaultDaysOrOptions.start,
				end: defaultDaysOrOptions.end,
				days: defaultDaysOrOptions.days ?? 30,
				interval: defaultDaysOrOptions.interval ?? "day",
				metric: defaultDaysOrOptions.metric ?? "visits"
			};
		}
		return {
			days: defaultDaysOrOptions,
			interval: defaultInterval,
			metric: defaultMetric
		};
	}, [defaultDaysOrOptions, defaultInterval, defaultMetric]);

	const range = useMemo(() => {
		if (opts.start && opts.end) {
			return { start: opts.start, end: opts.end };
		}
		const end = new Date();
		const start = new Date(end.getTime() - (opts.days ?? 30) * 24 * 60 * 60 * 1000);
		return { start: start.toISOString(), end: end.toISOString() };
	}, [opts.start, opts.end, opts.days]);
	const [data, setData] = useState({ data: [] });
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			setLoading(true);
			setError(null);
			try {
				const res = await fetchUsageTrends({ start: range.start, end: range.end, interval: opts.interval, metric: opts.metric });
				if (!cancelled) setData(res);
			} catch (e) {
				if (!cancelled) setError(e?.response?.data?.message || e.message || "Failed to load usage trends");
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => { cancelled = true; };
	}, [range.start, range.end, opts.interval, opts.metric]);

	return { data, loading, error };
}


